import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { toast } from 'sonner'
import { Pagination, type PageSize } from '../../components/ui/Pagination'
import { ExportDropdown } from '../../components/ui/ExportDropdown'
import { SearchSelect } from '../../components/ui/SearchSelect'
import { SEGMENT_LABELS } from './ClientiShopifyPage'
import { useAuthStore } from '../../stores/auth'
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react'
import { Kpi } from '../../components/ui/Kpi'
import { ETICHETE_CIFRE } from '@b4l/shared'
import {
  FINANCIAL_LABELS,
  FULFILLMENT_LABELS,
  StatusPlata,
} from '../../components/shopify/StatusPlata'

interface OrderRow {
  id: number
  shopifyId: string
  name: string
  email: string | null
  phone: string | null
  customerShopifyId: string | null
  customerFirstName: string | null
  customerLastName: string | null
  financialStatus: string | null
  fulfillmentStatus: string | null
  totalPrice: string
  totalDiscounts: string | null
  currency: string
  createdAtShopify: string
  cancelledAt: string | null
  /** Comanda expediata si nevanduta. Gol = vanduta. Vezi `lib/shopify/nevandut.ts` pe server. */
  motivNevandut: 'retur' | 'livrare_esuata' | 'plata_anulata' | null
  shippingCity: string | null
  shippingProvince: string | null
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  /** Bucatile din comanda (suma cantitatilor), nu numarul de linii. */
  itemCount: number
  discountCodes: string[]
}

interface OrdersResp {
  orders: OrderRow[]
  total: number
  /** `clients`: oameni diferiti din comenzile filtrate — cine a cumparat de doua ori e unul. */
  totals: {
    /** Venitul din produse, fara transport — ca „Venit produse" din Performanta produse. */
    productRevenue: string
    /** Tot ce s-a incasat pe comenzile vandute, cu transport. */
    revenue: string
    discount: string
    clients: number
    /** Comenzile vandute (fara retururi si nelivrate) — venitul si comanda medie se socotesc pe ele. */
    sold: number
    /** Insumate pe comenzile vandute: SKU-uri diferite din fiecare comanda, respectiv bucati. */
    products: number
    items: number
    returns: number
    undelivered: number
    /** Comenzile expediate din filtru — numitorul ratei de retur. */
    shipped: number
  }
}

interface OrderItem {
  id: number
  shopifyId: string
  sku: string | null
  title: string
  variantTitle: string | null
  quantity: number
  price: string
  totalDiscount: string
  vendor: string | null
}

interface DiscountUse {
  id: number
  code: string
  type: string
  amount: string
}

interface OrderDetailResp {
  order: OrderRow & {
    subtotalPrice: string | null
    totalShippingPrice: string | null
    totalTax: string | null
    referringSite: string | null
    landingSite: string | null
    sourceName: string | null
    paymentGateway: string | null
    closedAt: string | null
    processedAt: string | null
    tags: string[]
    items: OrderItem[]
    discountUses: DiscountUse[]
  }
  customer: {
    id: number
    shopifyId: string
    email: string | null
    firstName: string | null
    lastName: string | null
    ordersCount: number
    totalSpent: string
    avgOrderValue: string
    segment: string | null
    daysSinceLastOrder: number | null
    cohortMonth: string | null
  } | null
}

const FULFILLMENT_COLORS: Record<string, string> = {
  fulfilled: 'bg-b4l-green-light text-b4l-green-dark',
  partial: 'bg-b4l-amber-light text-b4l-amber-dark',
  unfulfilled: 'bg-b4l-bg text-b4l-text-muted',
  restocked: 'bg-b4l-purple-light text-b4l-purple-dark',
}

const SEGMENTS = ['VIP', 'recurrent', 'active', 'at_risk', 'reactivation', 'churned', 'one_time']

export function ComenziShopifyPage() {
  /** Exportul e un drept PERSONAL: nu vine cu niciun rol, nici cu `admin`. */
  const poateExporta = useAuthStore((s) => s.permissions).includes('date.clienti:export')
  const [search, setSearch] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [financialStatus, setFinancialStatus] = useState('')
  const [fulfillmentStatus, setFulfillmentStatus] = useState('')
  const [city, setCity] = useState('')
  const [voucherCode, setVoucherCode] = useState('')
  const [sku, setSku] = useState('')
  const [segment, setSegment] = useState('')
  const [includeCancelled, setIncludeCancelled] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<PageSize>(10)
  const [detailId, setDetailId] = useState<number | null>(null)
  const [filtreDeschise, setFiltreDeschise] = useState(false)

  const params = new URLSearchParams()
  params.set('limit', String(pageSize))
  params.set('offset', String(page * pageSize))
  if (search.trim()) params.set('search', search.trim())
  if (dateFrom) params.set('dateFrom', dateFrom)
  if (dateTo) params.set('dateTo', dateTo)
  if (financialStatus) params.set('financialStatus', financialStatus)
  if (fulfillmentStatus) params.set('fulfillmentStatus', fulfillmentStatus)
  if (city.trim()) params.set('city', city.trim())
  if (voucherCode.trim()) params.set('voucherCode', voucherCode.trim())
  if (sku) params.set('sku', sku)
  if (segment) params.set('segment', segment)
  if (includeCancelled) params.set('includeCancelled', 'true')

  const query = useQuery<OrdersResp>({
    queryKey: ['shopify-orders', params.toString()],
    queryFn: async () => (await api.get<OrdersResp>(`/shopify/orders?${params.toString()}`)).data,
  })
  const skuQuery = useQuery({
    queryKey: ['shopify-order-skus'],
    queryFn: async () =>
      (await api.get<{ skus: Array<{ sku: string; name: string }> }>('/shopify/order-skus')).data,
    staleTime: 10 * 60_000,
  })

  const detailQuery = useQuery<OrderDetailResp>({
    queryKey: ['shopify-order', detailId],
    queryFn: async () => (await api.get<OrderDetailResp>(`/shopify/orders/${detailId}`)).data,
    enabled: detailId !== null,
  })

  const resetFilters = () => {
    setSearch('')
    setDateFrom('')
    setDateTo('')
    setFinancialStatus('')
    setFulfillmentStatus('')
    setCity('')
    setVoucherCode('')
    setSku('')
    setSegment('')
    setIncludeCancelled(false)
    setPage(0)
  }

  const onAnyFilterChange = () => setPage(0)
  /** Din cardul „Comenzi esuate": partea pe care ai apasat devine filtrul listei. */
  const filtreazaPlata = (v: string) => {
    setFinancialStatus(v)
    onAnyFilterChange()
  }

  /*
    Filtrele stau ascunse sub buton (Madalin, 09.10.2026: „pagina e prea aglomerata"). Cele
    aplicate raman insa la vedere, ca etichete langa buton — altfel cineva ar citi cifrele de mai
    jos fara sa stie ca sunt filtrate.
  */
  const filtreActive: Array<{ eticheta: string; valoare: string; scoate: () => void }> = []
  const adaugaFiltru = (eticheta: string, valoare: string, scoate: () => void) =>
    filtreActive.push({
      eticheta,
      valoare,
      scoate: () => {
        scoate()
        onAnyFilterChange()
      },
    })
  if (search.trim()) adaugaFiltru('Căutare', search.trim(), () => setSearch(''))
  if (dateFrom) adaugaFiltru('De la', dataRo(dateFrom), () => setDateFrom(''))
  if (dateTo) adaugaFiltru('Până la', dataRo(dateTo), () => setDateTo(''))
  if (city.trim()) adaugaFiltru('Oraș', city.trim(), () => setCity(''))
  if (voucherCode.trim()) adaugaFiltru('Voucher', voucherCode.trim(), () => setVoucherCode(''))
  if (sku)
    adaugaFiltru('Produs', skuQuery.data?.skus.find((p) => p.sku === sku)?.name ?? sku, () =>
      setSku(''),
    )
  if (financialStatus)
    adaugaFiltru('Plată', ETICHETE_FILTRU_PLATA[financialStatus] ?? financialStatus, () =>
      setFinancialStatus(''),
    )
  if (fulfillmentStatus)
    adaugaFiltru(
      'Expediere',
      fulfillmentStatus === 'null'
        ? 'Neexpediat'
        : (FULFILLMENT_LABELS[fulfillmentStatus] ?? fulfillmentStatus),
      () => setFulfillmentStatus(''),
    )
  if (segment) adaugaFiltru('Segment', SEGMENT_LABELS[segment] ?? segment, () => setSegment(''))
  if (includeCancelled) adaugaFiltru('Anulate', 'incluse', () => setIncludeCancelled(false))

  const total = query.data?.total ?? 0
  const totals = query.data?.totals
  const visibleCount = query.data?.orders.length ?? 0

  const [exporting, setExporting] = useState(false)
  const exportOrders = async (currentPageOnly: boolean) => {
    try {
      setExporting(true)
      const exportParams = new URLSearchParams(params)
      if (!currentPageOnly) {
        exportParams.delete('limit')
        exportParams.delete('offset')
      }
      await downloadXlsx(`/shopify/exports/orders?${exportParams.toString()}`)
      toast.success('Export descărcat')
    } catch {
      toast.error('Eroare la export')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Comenzi Shopify</h1>
        {/*
          Butonul dispare pentru cine n-are dreptul. Nu e protectie — aceea e pe ruta, care refuza
          oricum — ci politete: sa nu apesi ceva ce vine inapoi cu „interzis".
        */}
        {poateExporta && (
          <ExportDropdown
            loading={exporting}
            pageCount={visibleCount}
            totalCount={total}
            onExportPage={() => exportOrders(true)}
            onExportAll={() => exportOrders(false)}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFiltreDeschise((v) => !v)}
          aria-expanded={filtreDeschise}
          aria-controls="filtre-comenzi"
          className={`inline-flex items-center gap-2 rounded-md border-2 border-b4l-blue px-5 py-2.5 text-sm font-semibold uppercase tracking-wide shadow-sm transition ${
            filtreDeschise
              ? 'bg-b4l-blue text-white'
              : 'bg-white text-b4l-blue-dark hover:bg-b4l-blue-light'
          }`}
        >
          <SlidersHorizontal size={18} />
          Filtrează căutarea
          {filtreActive.length > 0 && (
            <span
              className={`rounded-full px-2 py-0.5 text-xs ${
                filtreDeschise ? 'bg-white text-b4l-blue-dark' : 'bg-b4l-blue text-white'
              }`}
            >
              {filtreActive.length}
            </span>
          )}
          <ChevronDown
            size={18}
            className={`transition-transform ${filtreDeschise ? 'rotate-180' : ''}`}
          />
        </button>
        {!filtreDeschise &&
          filtreActive.map((f) => (
            <span
              key={f.eticheta}
              className="inline-flex max-w-xs items-center gap-1 rounded-full border border-b4l-blue/30 bg-b4l-blue-light py-1 pl-3 pr-1 text-xs text-b4l-blue-dark"
            >
              <span className="text-b4l-text-muted">{f.eticheta}:</span>
              <span className="truncate font-medium" title={f.valoare}>
                {f.valoare}
              </span>
              <button
                type="button"
                onClick={f.scoate}
                aria-label={`Scoate filtrul ${f.eticheta}`}
                className="rounded-full p-0.5 hover:bg-b4l-blue/20"
              >
                <X size={14} />
              </button>
            </span>
          ))}
        {!filtreDeschise && filtreActive.length > 1 && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs text-b4l-text-muted underline hover:text-b4l-text"
          >
            Șterge toate
          </button>
        )}
      </div>

      {/* Filtrele se aplica pe loc, la fiecare schimbare; butoanele de jos doar inchid sau golesc. */}
      {filtreDeschise && (
        <div id="filtre-comenzi">
          <Card>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-4">
              <Field
                label="Căutare (nr/email/nume/tel)"
                placeholder="ex: #1234, ana@..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  onAnyFilterChange()
                }}
              />
              <Field
                label="De la"
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value)
                  onAnyFilterChange()
                }}
              />
              <Field
                label="Până la"
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value)
                  onAnyFilterChange()
                }}
              />
              <Field
                label="Oraș"
                placeholder="ex: București"
                value={city}
                onChange={(e) => {
                  setCity(e.target.value)
                  onAnyFilterChange()
                }}
              />
              <Field
                label="Voucher"
                placeholder="ex: WELCOME10"
                value={voucherCode}
                onChange={(e) => {
                  setVoucherCode(e.target.value)
                  onAnyFilterChange()
                }}
              />
              {/*
            Lista, nu camp liber: la marketing se stie numele produsului, nu SKU-ul. Cautarea
            din lista gaseste dupa oricare dintre ele, fiindca eticheta le are pe amandoua.
          */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-b4l-text-dim">
                  Produs (nume sau SKU)
                </label>
                <SearchSelect<string>
                  options={(skuQuery.data?.skus ?? []).map((p) => ({
                    value: p.sku,
                    label: `${p.name} · ${p.sku}`,
                  }))}
                  value={sku || null}
                  onChange={(v) => {
                    setSku(v ?? '')
                    onAnyFilterChange()
                  }}
                  placeholder={skuQuery.isLoading ? 'Se încarcă…' : 'Toate'}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-b4l-text-dim">Stare plată</label>
                <select
                  value={financialStatus}
                  onChange={(e) => {
                    setFinancialStatus(e.target.value)
                    onAnyFilterChange()
                  }}
                  className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
                >
                  <option value="">Toate</option>
                  <option value="paid">Plătit</option>
                  <option value="pending">În așteptare</option>
                  <option value="partially_paid">Plătit parțial</option>
                  <option value="refunded">Rambursat</option>
                  <option value="partially_refunded">Rambursat parțial</option>
                  <option value="voided">Anulat</option>
                  <option value="nevandute">Eșuate (nelivrate + retur)</option>
                  <option value="nelivrata">Nelivrată</option>
                  <option value="retur">Retur</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-b4l-text-dim">Stare expediere</label>
                <select
                  value={fulfillmentStatus}
                  onChange={(e) => {
                    setFulfillmentStatus(e.target.value)
                    onAnyFilterChange()
                  }}
                  className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
                >
                  <option value="">Toate</option>
                  <option value="fulfilled">Expediat</option>
                  <option value="partial">Expediat parțial</option>
                  <option value="null">Neexpediat</option>
                  <option value="restocked">Returnat în stoc</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-b4l-text-dim">Segment client</label>
                <select
                  value={segment}
                  onChange={(e) => {
                    setSegment(e.target.value)
                    onAnyFilterChange()
                  }}
                  className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
                >
                  <option value="">Toate</option>
                  {SEGMENTS.map((s) => (
                    <option key={s} value={s}>
                      {SEGMENT_LABELS[s] ?? s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={includeCancelled}
                    onChange={(e) => {
                      setIncludeCancelled(e.target.checked)
                      onAnyFilterChange()
                    }}
                  />
                  Include anulate
                </label>
              </div>
            </div>
            <div className="mt-3 flex justify-end gap-2 border-t border-b4l-border pt-3">
              <Button
                variant="secondary"
                onClick={resetFilters}
                disabled={filtreActive.length === 0}
              >
                Șterge filtrele
              </Button>
              <Button onClick={() => setFiltreDeschise(false)}>Gata</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Culorile cardurilor: vezi `components/ui/Kpi.tsx`. Cele de aceeasi culoare stau alaturi. */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Kpi ton="comenzi" label="Comenzi (filtru)" value={total.toLocaleString('ro-RO')} />
        {/*
          Produsele sunt tipurile diferite (SKU), itemii sunt bucatile: 2 × crema + 1 × ser = 2
          produse, 3 itemi. Pe comenzile vandute, ca si comanda medie.
        */}
        <Kpi
          ton="comenzi"
          label="Produse / comandă"
          value={medie(totals?.products, totals?.sold)}
          detaliu="tipuri diferite (SKU)"
          explicatie="Câte produse diferite (SKU-uri) are în medie o comandă vândută. Ex.: 2 × cremă + 1 × ser = 2 produse."
        />
        <Kpi
          ton="comenzi"
          label="Itemi / comandă"
          value={medie(totals?.items, totals?.sold)}
          detaliu="bucăți în total"
          explicatie="Câte bucăți are în medie o comandă vândută, oricare ar fi produsul. Ex.: 2 × cremă + 1 × ser = 3 itemi."
        />
        <Kpi ton="clienti" label="Clienți" value={(totals?.clients ?? 0).toLocaleString('ro-RO')} />
        <Kpi
          ton="venit"
          label="Venit produse (RON)"
          value={numar(Math.round(Number(totals?.productRevenue ?? '0')))}
          detaliu={
            totals &&
            `${ETICHETE_CIFRE.venitProduseShopify.detaliu} · cu transport: ${numar(Math.round(Number(totals.revenue)))}`
          }
          explicatie="Valoarea produselor vândute, după reduceri, cu TVA, fără transport, după data comenzii — aceeași cifră ca pe Performanță produse. „Cu transport” e tot ce s-a încasat pe comenzile vândute."
        />
        {/* Pe comenzile vandute: un retur sau o nelivrata n-au adus bani. */}
        <Kpi
          ton="venit"
          label="Comandă medie (RON)"
          value={
            (totals?.sold ?? 0) > 0
              ? (Number(totals?.productRevenue ?? '0') / (totals?.sold ?? 1)).toLocaleString(
                  'ro-RO',
                  { maximumFractionDigits: 0 },
                )
              : '-'
          }
          detaliu="fără transport"
        />
        <Kpi
          ton="discount"
          label="Discount acordat"
          value={Number(totals?.discount ?? '0').toLocaleString('ro-RO', {
            maximumFractionDigits: 0,
          })}
        />
        {/*
          Toate comenzile plecate care nu s-au vandut, intr-o singura rata (Madalin, 09.10.2026).
          Cardul era „Rata de retur" si numara doar retururile: 0,1%, cand de fapt aproape o
          comanda din zece nu ajungea vanzare. O comanda „Nelivrata" poate fi refuzata, neridicata
          sau anulata dupa expediere — din datele Shopify nu le putem deosebi sigur, deci nu le
          ghicim; le aratam impreuna, cu cele doua parti dedesubt. Fiecare parte deschide lista.
        */}
        <Kpi
          ton="retur"
          label="Comenzi eșuate"
          value={procent((totals?.returns ?? 0) + (totals?.undelivered ?? 0), totals?.shipped)}
          explicatie="Comenzi care au plecat la client și nu s-au transformat în vânzare: colet refuzat sau neridicat, plată anulată după expediere, ori produse returnate cu banii dați înapoi. Comenzile anulate înainte de expediere nu intră aici."
          detaliu={
            totals && (
              <>
                {numar(totals.returns + totals.undelivered)} din {numar(totals.shipped)} expediate
                <br />
                <button
                  type="button"
                  onClick={() => filtreazaPlata('nelivrata')}
                  className="underline decoration-dotted hover:text-b4l-red-dark"
                  title="Arată comenzile nelivrate"
                >
                  {numar(totals.undelivered)} nelivrate
                </button>
                {' · '}
                <button
                  type="button"
                  onClick={() => filtreazaPlata('retur')}
                  className="underline decoration-dotted hover:text-b4l-red-dark"
                  title="Arată comenzile returnate"
                >
                  {numar(totals.returns)} returnate
                </button>
              </>
            )
          }
        />
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs font-semibold text-b4l-text-dim">
                <th className="px-2 py-2">Comandă</th>
                <th className="px-2 py-2">Data</th>
                <th className="px-2 py-2">Client</th>
                <th className="px-2 py-2 text-right">Total</th>
                <th className="px-2 py-2">Plată</th>
                <th className="px-2 py-2">Expediere</th>
                <th className="px-2 py-2">Oraș</th>
                <th className="px-2 py-2">UTM</th>
                <th className="px-2 py-2">Voucher</th>
                <th className="px-2 py-2 text-right">Itemi</th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading && (
                <tr>
                  <td colSpan={10} className="px-2 py-6 text-center text-b4l-text-muted">
                    Se încarcă...
                  </td>
                </tr>
              )}
              {query.data?.orders.length === 0 && !query.isLoading && (
                <tr>
                  <td colSpan={10} className="px-2 py-6 text-center text-b4l-text-muted">
                    Niciun rezultat
                  </td>
                </tr>
              )}
              {query.data?.orders.map((o) => {
                const customerName =
                  o.customerFirstName || o.customerLastName
                    ? `${o.customerFirstName ?? ''} ${o.customerLastName ?? ''}`.trim()
                    : (o.email ?? '(fără date)')
                return (
                  <tr
                    key={o.id}
                    onClick={() => setDetailId(o.id)}
                    className={`cursor-pointer border-b border-b4l-border hover:bg-b4l-bg-soft ${
                      o.cancelledAt ? 'text-b4l-text-muted' : ''
                    }`}
                  >
                    <td className="px-2 py-1.5 font-mono text-xs">{o.name}</td>
                    <td className="px-2 py-1.5 text-xs">
                      {new Date(o.createdAtShopify).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="text-xs font-medium">{customerName}</div>
                      <div className="text-[10px] text-b4l-text-muted">{o.email}</div>
                    </td>
                    <td className="px-2 py-1.5 text-right font-medium">
                      {Number(o.totalPrice).toFixed(2)} {o.currency}
                    </td>
                    <td className="px-2 py-1.5">
                      <StatusPlata
                        financialStatus={o.financialStatus}
                        motivNevandut={o.motivNevandut}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <span
                        className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                          FULFILLMENT_COLORS[o.fulfillmentStatus ?? 'unfulfilled'] ?? 'bg-b4l-bg'
                        }`}
                      >
                        {FULFILLMENT_LABELS[o.fulfillmentStatus ?? 'unfulfilled'] ?? 'Neexpediat'}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-xs">{o.shippingCity ?? '-'}</td>
                    <td className="px-2 py-1.5 text-[10px] text-b4l-text-muted">
                      {o.utmSource ? `${o.utmSource}/${o.utmCampaign ?? '-'}` : '-'}
                    </td>
                    <td className="px-2 py-1.5">
                      {o.discountCodes.length > 0 ? (
                        <span className="font-mono text-[10px]">{o.discountCodes.join(', ')}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs">{o.itemCount}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-3">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s)
              setPage(0)
            }}
            itemLabel="comenzi"
          />
        </div>
      </Card>

      <Modal
        open={detailId !== null}
        onClose={() => setDetailId(null)}
        title="Detalii comandă"
        size="xl"
      >
        {detailQuery.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă...</p>}
        {detailQuery.data && <OrderDetail data={detailQuery.data} />}
      </Modal>
    </div>
  )
}

function OrderDetail({ data }: { data: OrderDetailResp }) {
  const { order, customer } = data
  const itemsSubtotal = order.items.reduce(
    (s, it) => s + Number(it.price) * it.quantity - Number(it.totalDiscount),
    0,
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-mono text-lg font-semibold">{order.name}</div>
          <div className="text-xs text-b4l-text-muted">
            {new Date(order.createdAtShopify).toLocaleString('en-GB')}
          </div>
          {order.cancelledAt && (
            <div className="mt-1 inline-block rounded bg-b4l-red-light px-2 py-0.5 text-xs text-b4l-red-dark">
              Anulată {new Date(order.cancelledAt).toLocaleDateString('en-GB')}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs text-b4l-text-muted">Total</div>
          <div className="text-2xl font-semibold">
            {Number(order.totalPrice).toFixed(2)} {order.currency}
          </div>
          <div className="flex flex-wrap gap-1 justify-end mt-1">
            <StatusPlata
              financialStatus={order.financialStatus}
              motivNevandut={order.motivNevandut}
            />
            <span
              className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                FULFILLMENT_COLORS[order.fulfillmentStatus ?? 'unfulfilled'] ?? 'bg-b4l-bg'
              }`}
            >
              {FULFILLMENT_LABELS[order.fulfillmentStatus ?? 'unfulfilled'] ?? 'Neexpediat'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded border border-b4l-border p-3">
          <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">Client</div>
          <div className="text-sm font-medium">
            {order.customerFirstName || order.customerLastName
              ? `${order.customerFirstName ?? ''} ${order.customerLastName ?? ''}`.trim()
              : '(fără nume)'}
          </div>
          <div className="text-xs text-b4l-text-muted">{order.email}</div>
          <div className="text-xs text-b4l-text-muted">{order.phone}</div>
          <div className="text-xs text-b4l-text-muted">
            {order.shippingCity}
            {order.shippingProvince && `, ${order.shippingProvince}`}
          </div>
          {customer && (
            <div className="mt-2 border-t border-b4l-border pt-2 text-xs">
              <div className="flex justify-between">
                <span className="text-b4l-text-muted">Segment:</span>
                <span className="font-medium">
                  {/*
                    Eticheta tradusa, aceeasi ca in pagina Clienti. Aici se afisa cheia bruta din
                    baza — „at_risk", „churned" — adica exact ce nu trebuie sa vada cine citeste un
                    raport. Harta se importa, nu se copiaza: doua liste care spun acelasi lucru se
                    despart la prima modificare.
                  */}
                  {customer.segment ? (SEGMENT_LABELS[customer.segment] ?? customer.segment) : '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-b4l-text-muted">Total comenzi:</span>
                <span>{customer.ordersCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-b4l-text-muted">LTV:</span>
                <span>{Number(customer.totalSpent).toFixed(0)} RON</span>
              </div>
              <div className="flex justify-between">
                <span className="text-b4l-text-muted">AOV:</span>
                <span>{Number(customer.avgOrderValue).toFixed(0)} RON</span>
              </div>
              <div className="mt-1">
                <Link
                  to={`/marketing/shopify/clienti`}
                  className="text-xs text-b4l-blue-dark hover:underline"
                >
                  Vezi profil client →
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="rounded border border-b4l-border p-3">
          <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">
            Sursă & atribuție
          </div>
          <div className="space-y-1 text-xs">
            <Row label="UTM Source" value={order.utmSource} />
            <Row label="UTM Medium" value={order.utmMedium} />
            <Row label="UTM Campaign" value={order.utmCampaign} />
            <Row label="Canal de vânzare" value={order.sourceName} />
            <Row label="Site de proveniență" value={order.referringSite} />
            <Row label="Metodă de plată" value={order.paymentGateway} />
            {order.tags.length > 0 && (
              <div>
                <span className="text-b4l-text-muted">Etichete:</span>{' '}
                <span className="font-mono">{order.tags.join(', ')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">
          Produse comandate
        </div>
        <div className="overflow-x-auto rounded border border-b4l-border">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-b4l-border bg-b4l-bg-soft text-left">
                <th className="px-2 py-1.5">SKU</th>
                <th className="px-2 py-1.5">Produs</th>
                <th className="px-2 py-1.5 text-right">Cant.</th>
                <th className="px-2 py-1.5 text-right">Preț unit.</th>
                <th className="px-2 py-1.5 text-right">Discount</th>
                <th className="px-2 py-1.5 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((it) => {
                const sub = Number(it.price) * it.quantity - Number(it.totalDiscount)
                return (
                  <tr key={it.id} className="border-b border-b4l-border">
                    <td className="px-2 py-1.5 font-mono">{it.sku ?? '-'}</td>
                    <td className="px-2 py-1.5">
                      {it.title}
                      {it.variantTitle && (
                        <span className="text-b4l-text-muted"> · {it.variantTitle}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">{it.quantity}</td>
                    <td className="px-2 py-1.5 text-right">{Number(it.price).toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right text-b4l-red-dark">
                      {Number(it.totalDiscount) > 0
                        ? `-${Number(it.totalDiscount).toFixed(2)}`
                        : '-'}
                    </td>
                    <td className="px-2 py-1.5 text-right font-medium">{sub.toFixed(2)}</td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="bg-b4l-bg-soft text-xs">
                <td colSpan={5} className="px-2 py-1.5 text-right text-b4l-text-muted">
                  Subtotal produse:
                </td>
                <td className="px-2 py-1.5 text-right font-medium">
                  {itemsSubtotal.toFixed(2)} {order.currency}
                </td>
              </tr>
              {order.totalShippingPrice && Number(order.totalShippingPrice) > 0 && (
                <tr className="text-xs">
                  <td colSpan={5} className="px-2 py-1.5 text-right text-b4l-text-muted">
                    Transport:
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {Number(order.totalShippingPrice).toFixed(2)} {order.currency}
                  </td>
                </tr>
              )}
              {order.totalTax && Number(order.totalTax) > 0 && (
                <tr className="text-xs">
                  <td colSpan={5} className="px-2 py-1.5 text-right text-b4l-text-muted">
                    TVA:
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {Number(order.totalTax).toFixed(2)} {order.currency}
                  </td>
                </tr>
              )}
              <tr className="text-sm font-semibold">
                <td colSpan={5} className="px-2 py-1.5 text-right">
                  TOTAL:
                </td>
                <td className="px-2 py-1.5 text-right">
                  {Number(order.totalPrice).toFixed(2)} {order.currency}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {order.discountUses.length > 0 && (
        <div>
          <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">
            Vouchere folosite
          </div>
          <ul className="space-y-1">
            {order.discountUses.map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between rounded border border-b4l-border bg-b4l-amber-light/30 px-3 py-1.5 text-xs"
              >
                <span className="font-mono font-medium">{d.code}</span>
                <span className="text-b4l-text-muted">
                  {d.type} · -{Number(d.amount).toFixed(2)} RON
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-b4l-text-muted">{label}:</span>
      <span className="truncate font-mono text-[11px]">{value ?? '-'}</span>
    </div>
  )
}

/** `parte / tot` ca procent cu o zecimala; „-" cand n-avem din ce. */
function procent(parte: number | undefined, tot: number | undefined): string {
  if (!tot) return '-'
  return `${((100 * (parte ?? 0)) / tot).toLocaleString('ro-RO', { maximumFractionDigits: 1 })}%`
}

/** Etichetele din lista „Status plata", pentru eticheta filtrului activ. */
const ETICHETE_FILTRU_PLATA: Record<string, string> = {
  ...FINANCIAL_LABELS,
  retur: 'Retur',
  nelivrata: 'Nelivrată',
  nevandute: 'Eșuate',
}

const numar = (n: number) => n.toLocaleString('ro-RO')

/** `2026-10-01` → `01.10.2026` */
function dataRo(iso: string): string {
  const [a, l, z] = iso.split('-')
  return `${z}.${l}.${a}`
}

function medie(parte: number | undefined, tot: number | undefined): string {
  if (!tot) return '-'
  return ((parte ?? 0) / tot).toLocaleString('ro-RO', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
}
